/**
 * Aviso de crédito del cliente
 *
 * Se muestra en el punto de venta al escoger un cliente que se pasó del límite
 * de crédito o que arrastra facturas vencidas. Es solo informativo: no bloquea
 * la venta ni el método de pago, porque quien decide si le fía es el negocio,
 * no el sistema.
 */

import { AlertTriangle, Clock, CreditCard } from 'lucide-react'

import { cn, formatCurrency, formatDate } from '@/lib/utils'
import { t } from '@/i18n'

export interface ClientAccountSummary {
  total_debt: number
  available_credit: number
  credit_limit: number
  pending_bills_count: number
  available_balance: number
  has_credit_limit: boolean
  credit_limit_exceeded: boolean
  overdue_count: number
  overdue_amount: number
  max_days_overdue: number
  oldest_overdue_date: string | null
}

interface ClientCreditAlertProps {
  summary?: ClientAccountSummary | null
  className?: string
}

export function ClientCreditAlert({ summary, className }: ClientCreditAlertProps) {
  if (!summary) return null

  const limitExceeded = summary.credit_limit_exceeded
  const hasOverdue = summary.overdue_count > 0
  if (!limitExceeded && !hasOverdue) return null

  const overAmount = summary.total_debt - summary.credit_limit

  return (
    <div
      className={cn(
        'rounded-lg border border-amber-400/60 bg-amber-50 p-3 text-amber-900',
        'dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100',
        className
      )}
      role="status"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 space-y-1.5 text-sm">
          <p className="font-medium">{t('pos.creditAlertTitle')}</p>

          {limitExceeded && (
            <p className="flex flex-wrap items-baseline gap-x-1.5">
              <CreditCard className="h-3.5 w-3.5 shrink-0 self-center" />
              <span className="font-medium">{t('pos.creditLimitExceeded')}:</span>
              <span>
                {t('pos.creditLimitDetail', {
                  debt: formatCurrency(summary.total_debt),
                  limit: formatCurrency(summary.credit_limit),
                })}
                {overAmount > 0 && (
                  <>
                    {', '}
                    {t('pos.creditLimitOver', { amount: formatCurrency(overAmount) })}
                  </>
                )}
                .
              </span>
            </p>
          )}

          {hasOverdue && (
            <p className="flex flex-wrap items-baseline gap-x-1.5">
              <Clock className="h-3.5 w-3.5 shrink-0 self-center" />
              <span>
                {summary.overdue_count === 1
                  ? t('pos.overdueBillsOne', {
                      amount: formatCurrency(summary.overdue_amount),
                    })
                  : t('pos.overdueBillsMany', {
                      count: summary.overdue_count,
                      amount: formatCurrency(summary.overdue_amount),
                    })}
                {summary.oldest_overdue_date && (
                  <>
                    {', '}
                    {t('pos.overdueOldest', {
                      date: formatDate(summary.oldest_overdue_date),
                      days: summary.max_days_overdue,
                    })}
                  </>
                )}
                .
              </span>
            </p>
          )}

          <p className="text-xs opacity-80">{t('pos.creditAlertHint')}</p>
        </div>
      </div>
    </div>
  )
}
